import React from 'react';

interface ErrorBoundaryProps {
  children: React.ReactNode;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    this.props.onError?.(error, errorInfo);
  }

  handleRetry = (): void => {
    this.setState({ hasError: false, error: null });
  };

  render(): React.ReactNode {
    if (this.state.hasError) {
      const isDev = typeof process !== 'undefined' && process.env?.NODE_ENV !== 'production';
      return (
        <div role="alert" aria-live="assertive" className="p-4 text-center">
          <p className="text-sm text-red-600 font-semibold mb-2">Something went wrong</p>
          <p className="text-xs text-gray-600 mb-3">
            {isDev
              ? this.state.error?.message || 'An unexpected error occurred.'
              : 'An unexpected error occurred. Please try again.'}
          </p>
          <button
            onClick={this.handleRetry}
            type="button"
            className="px-3 py-1.5 text-xs rounded bg-blue-600 text-white hover:bg-blue-700 focus:outline-2 focus:outline-blue-500"
          >
            Try again
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
